import { RootState } from "redux/store";
import RequestStatus from "redux/slices/RequestStatus";
import {
  CalcCaseProperty,
  Case,
  CaseClientStatus,
  CaseDisplayGroup,
  CaseReviewStatus,
  ClientLens,
  CustomCaseProperty,
  CustomDocumentProperty,
  CustomPropertyQueryInput,
  LensChronologyConfigView,
  LensOutcomeStatusConfigurationView,
  LookerEmbedItemsResponse,
  NamedUser,
  OutcomeStatus,
  ProfileExportConfigView,
  SectionReviewState,
  SectionValueMatchMode,
  UserType,
  ValidationStatus,
} from "generated/graphql";
import { CaseFilterQueryParam, LensHomeFeatures } from "./clientLens.slice";
import { createSelector } from "@reduxjs/toolkit";
import { selectIntakes } from "../case";
import CaseReports from "../CaseReports";
import { CASE_DISPLAY_GROUP_DEFAULT_NAME } from "routes/lens-editor/custom-properties/case-display-groups.loader";
import { isNotUndefined, parseSafeInt } from "Backend";
import addHours from "date-fns/addHours";

export const selectClientLensFormId = (state: RootState) => state.clientLens.clientLens.formId;

export const selectClientLens = (state: RootState): ClientLens => state.clientLens.clientLens;
export const selectClientLensSBREnabled = (state: RootState): boolean | undefined =>
  state.clientLens.sectionBasedReviewEnabled;
export const selectClientLensStatus = (state: RootState): RequestStatus => state.clientLens.status;
export const selectClientLensAssignees = (state: RootState): NamedUser[] => state.clientLens.assignees;
export const selectClientLensCases = (state: RootState): Case[] => state.clientLens.cases;
export const selectClientLensExportConfigs = (state: RootState): ProfileExportConfigView[] =>
  state.clientLens.profileExportConfigs;
export const selectClientLensExportConfigsStatus = (state: RootState): RequestStatus =>
  state.clientLens.profileExportConfigsStatus;
export const selectClientLensCasesTotal = (state: RootState): number => state.clientLens.casesTotal;
export const selectClientLensCasesStatus = (state: RootState): RequestStatus => state.clientLens.casesStatus;
export const selectClientLensLitigationName = (state: RootState): string =>
  state.clientLens.clientLens.lens.litigationName;
export const selectClientLensCaseDefaultGroup = (state: RootState): CaseDisplayGroup | undefined =>
  state.clientLens.caseDisplayGroups.find((cdg) => cdg.name === CASE_DISPLAY_GROUP_DEFAULT_NAME);
export const selectClientLensCaseInfoGroups = (state: RootState): CaseDisplayGroup[] =>
  state.clientLens.caseDisplayGroups.filter((cdg) => cdg.locations.find((l) => l.caseInfo));
export const selectClientLensCaseHomeGroups = (state: RootState): CaseDisplayGroup[] =>
  state.clientLens.caseDisplayGroups.filter((cdg) => cdg.locations.find((l) => l.caseHome));
export const selectClientLensSectionGroups = (state: RootState): CaseDisplayGroup[] =>
  state.clientLens.caseDisplayGroups.filter(
    (cdg) => cdg.locations.find((l) => l.sectionId !== undefined && l.sectionId !== null) !== undefined
  );

export type CaseFilterQueryParams = {
  textQuery?: string;
  sectionStatuses?: SectionReviewState[];
  sectionIds?: number[];
  assigneeId?: number;
  unassigned?: boolean;
  sectionsUnassignedOnly?: boolean;
  sectionAssigneeIds: number[];
  caseStatus?: CaseClientStatus | "all";
  reviewStatuses?: CaseReviewStatus[];
  outcomeStatuses?: OutcomeStatus[];
  validationStatuses?: ValidationStatus[];
  plaintiffFirmId?: number;
  reviewStatusReason?: string;
  page: number;
  rowsPerPage: number;
  hasDocuments?: boolean;
  sectionMatchMode?: SectionValueMatchMode;
  caseProperties: CustomPropertyQueryInput[];
  caseCalcs: CustomPropertyQueryInput[];
};

export function parseCaseFilterQueryParams(
  searchParams: URLSearchParams,
  SBREnabled: boolean = false,
  defaultSectionStatuses: SectionReviewState[] = []
): CaseFilterQueryParams {
  const search = searchParams.get(CaseFilterQueryParam.SEARCH) || undefined;
  const assignee = parseSafeInt(searchParams.get(CaseFilterQueryParam.ASSIGNEE_ID));
  const sectionAssignees = SBREnabled
    ? searchParams.getAll(CaseFilterQueryParam.SECTION_ASSIGNEES).map(parseSafeInt).filter(isNotUndefined)
    : [];
  const caseStatus = searchParams.get(CaseFilterQueryParam.CASE_STATUS) || CaseClientStatus.Active;
  const reviewStatuses: CaseReviewStatus[] = (() => {
    const urlReviewStatuses = searchParams.getAll(CaseFilterQueryParam.REVIEW_STATUS);
    const containsAll = urlReviewStatuses.includes("all");
    const defaultStatuses = SBREnabled ? [] : [CaseReviewStatus.New, CaseReviewStatus.NewDocuments];
    return containsAll
      ? []
      : urlReviewStatuses.length === 0
        ? defaultStatuses
        : (urlReviewStatuses as CaseReviewStatus[]);
  })();
  const firmId = searchParams.get(CaseFilterQueryParam.FIRM_ID) || undefined;
  const reviewStatusReason = searchParams.get(CaseFilterQueryParam.REVIEW_STATUS_REASON) || undefined;
  const page = searchParams.get(CaseFilterQueryParam.PAGE) || undefined;
  const rowsPerPage = searchParams.get(CaseFilterQueryParam.ROWS_PER_PAGE) || undefined;
  const paramSectionStatuses = searchParams.getAll(CaseFilterQueryParam.SECTION_STATUS);
  const sectionStatuses = paramSectionStatuses.length > 0 ? paramSectionStatuses : defaultSectionStatuses;
  const outcomeStatuses: OutcomeStatus[] = searchParams.getAll(CaseFilterQueryParam.OUTCOME_STATUS) as OutcomeStatus[];
  const validationStatuses: ValidationStatus[] = searchParams.getAll(
    CaseFilterQueryParam.VALIDATION_STATUS
  ) as ValidationStatus[];
  const sectionIds = searchParams.getAll(CaseFilterQueryParam.SECTION_ID);
  const hasDocuments = searchParams.get(CaseFilterQueryParam.HAS_DOCUMENTS) || undefined;
  const filterToUnassigned = assignee === -1;
  const filterToSectionsUnassigned = sectionAssignees.length > 0 && sectionAssignees.every((a) => a === -1);
  const sectionMatchMode =
    (searchParams.get(CaseFilterQueryParam.SECTION_MATCH_MODE) as SectionValueMatchMode) ?? SectionValueMatchMode.Any;
  const caseProperties: CustomPropertyQueryInput[] = Array.from(searchParams.entries())
    .filter(([key]) => key.indexOf("prop-") > -1)
    .map(([key, value]) => {
      return {
        exact: true,
        key: key.slice(key.indexOf("prop-") + 5),
        value,
      };
    });
  const caseCalcs: CustomPropertyQueryInput[] = Array.from(searchParams.entries())
    .filter(([key]) => key.indexOf("calc-") > -1)
    .map(([key, value]) => {
      return {
        exact: true,
        key: key.slice(key.indexOf("calc-") + 5),
        value,
      };
    });
  return {
    textQuery: search,
    assigneeId: !SBREnabled ? assignee : undefined,
    sectionAssigneeIds: sectionAssignees,
    unassigned: !SBREnabled && filterToUnassigned ? true : undefined,
    sectionsUnassignedOnly: SBREnabled && filterToSectionsUnassigned ? true : undefined,
    caseStatus: (caseStatus as CaseClientStatus) || undefined,
    reviewStatuses: !SBREnabled ? (reviewStatuses as CaseReviewStatus[]) || undefined : undefined,
    plaintiffFirmId: firmId ? parseInt(firmId) : undefined,
    reviewStatusReason,
    page: page ? parseInt(page) - 1 : 0,
    rowsPerPage: rowsPerPage ? parseInt(rowsPerPage) : 50,
    sectionStatuses: SBREnabled ? (sectionStatuses as SectionReviewState[]) : undefined,
    outcomeStatuses: SBREnabled ? outcomeStatuses : undefined,
    validationStatuses: SBREnabled ? validationStatuses : undefined,
    sectionIds: SBREnabled
      ? sectionIds !== undefined
        ? sectionIds.map(parseSafeInt).filter(isNotUndefined)
        : undefined
      : undefined,
    hasDocuments: hasDocuments === "true" ? true : hasDocuments === "false" ? false : undefined,
    caseProperties,
    caseCalcs,
    sectionMatchMode,
  };
}

export const selectClientLensCaseFilters = (state: RootState): string | undefined => {
  return state.clientLens.caseFilterParams;
};
export const selectProfileExportConfigs = (state: RootState): ProfileExportConfigView[] =>
  state.clientLens.profileExportConfigs;
export const selectProfileExportConfigsStatus = (state: RootState): RequestStatus =>
  state.clientLens.profileExportConfigsStatus;
export const selectChronologyConfig = (state: RootState): LensChronologyConfigView | undefined =>
  state.clientLens.chronologyConfig;
export const selectLensOutcomeStatusConfiguration = (
  state: RootState
): { lensOutcomeStatusConfigurations: LensOutcomeStatusConfigurationView[]; sbrEnabled: boolean | undefined } => ({
  lensOutcomeStatusConfigurations: state.clientLens.lensOutcomeStatusConfigurations,
  sbrEnabled: state.clientLens.sectionBasedReviewEnabled,
});

export const selectCustomPropertiesStatus = (state: RootState): RequestStatus =>
  state.clientLens.customPropertiesStatus;
export const selectCasePropertySchemas = (state: RootState): CustomCaseProperty[] => state.clientLens.caseProperties;
export const selectDocumentProperties = (state: RootState): CustomDocumentProperty[] =>
  state.clientLens.documentProperties;
export const selectCalcProperties = (state: RootState): CalcCaseProperty[] => state.clientLens.calcProperties;
export const selectVisibleDocumentProperties = createSelector([selectDocumentProperties], (dps) =>
  dps.filter((dp) => !dp.isHidden)
);
export const selectCaseDisplayGroups = (state: RootState): CaseDisplayGroup[] =>
  state.clientLens.caseDisplayGroups.slice().sort((a, b) => a.name.localeCompare(b.name));
export const selectCaseInsightsEnabled = (state: RootState): boolean =>
  state.clientLens.clientLens.lens.calcsEnabled || false;

export const selectVisibleDocumentPropertiesThatHaveValues = createSelector(
  [selectVisibleDocumentProperties, selectIntakes],
  (dps, intakes) => {
    return dps.filter((dp) => {
      const hasValue = intakes.some((i) => {
        const value = i.properties?.find((cp) => cp.key === dp.key)?.value;
        return value !== undefined && value !== null && value !== "";
      });
      return hasValue;
    });
  }
);

// Reports queries
export const selectReportSliceStatus = (state: RootState): RequestStatus => state.clientLens.reportsStatus;
export const selectLookerReports = (state: RootState): LookerEmbedItemsResponse => state.clientLens.lookerReports;

export const selectLookerSupplementalDataReport = createSelector([selectLookerReports], (lookerReports) =>
  lookerReports.embedItems.find((report) => report.name === CaseReports.SUPPLEMENTAL_DATA)
);
// Lens Home queries
export const selectLastViewedTab = (state: RootState): UserType | undefined => state.clientLens.lastViewedTab;
export const selectLastViewedFeature = (state: RootState): LensHomeFeatures => state.clientLens.lastViewedFeature;
// Lens Admin queries
export const selectRawPendingReviewsByAssignee = (state: RootState) => state.clientLens.pendingReviewsByAssignee;
export const selectPendingReviewsByAssignee = createSelector(
  [selectRawPendingReviewsByAssignee],
  (pendingReviewsByAssignee) => {
    return pendingReviewsByAssignee
      .map((r) => ({
        displayName: r.assignee.lastName[0]
          ? `${r.assignee.firstName} ${r.assignee.lastName[0]}`
          : r.assignee.firstName,
        email: r.assignee.email,
        firstName: r.assignee.firstName,
        lastName: r.assignee.lastName,
        count: r.count,
      }))
      .sort((a, b) => b.count - a.count);
  }
);
export const selectCountOfPendingReviews = createSelector([selectPendingReviewsByAssignee], (pendingReviews) =>
  pendingReviews.reduce((prev, a) => prev + a.count, 0)
);
export const selectCompletedTimeframe = (state: RootState) => state.clientLens.completedTimeframe;
export const selectCompletedReviewsByAssignee = (state: RootState) => {
  return state.clientLens.completedReviewsByAssignee.map((r) => ({
    displayName: r.reviewer.lastName[0] ? `${r.reviewer.firstName} ${r.reviewer.lastName[0]}` : r.reviewer.firstName,
    email: r.reviewer.email,
    firstName: r.reviewer.firstName,
    lastName: r.reviewer.lastName,
    count: r.count,
  }));
};
export const selectCountOfCompletedReviews = createSelector([selectCompletedReviewsByAssignee], (completedReviews) =>
  completedReviews.reduce((prev, a) => prev + a.count, 0)
);
export const selectCompletedReviewsTimeline = (state: RootState) =>
  state.clientLens.completedReviewsTimeline.map((review) => {
    const reviewDate = new Date(review.datetime);
    const utcDate = addHours(reviewDate, reviewDate.getTimezoneOffset() / 60);
    return {
      count: review.count,
      accumulatedCount: review.accumulatedCount,
      utcDate,
      datetime: review.datetime,
    };
  });
export const selectCountOfCompletedReviewsTimeline = createSelector(
  [selectCompletedReviewsTimeline],
  (completedReviewsTimeline) => {
    return completedReviewsTimeline.reduce((prev, a) => prev + a.count, 0);
  }
);
export const selectAdminTimeUnit = (state: RootState) => state.clientLens.adminTimeUnit;
export const selectAdminBackPeriod = (state: RootState) => state.clientLens.adminBackPeriod;
export const selectAdminTimeWhen = createSelector(
  [selectAdminTimeUnit, selectAdminBackPeriod],
  (timeUnit, backPeriod) => {
    const when = backPeriod === 0 ? "this" : "last";
    return `${when}${timeUnit}`;
  }
);
export const selectSBRAdminCaseStats = (state: RootState) => state.clientLens.sbrAdminStats;
//Lens Review queries
export const selectReviewerTimeframe = (state: RootState) => state.clientLens.reviewerTimeframe;
export const selectReviewerTimeUnit = (state: RootState) => state.clientLens.reviewerTimeUnit;
export const selectReviewerBackPeriod = (state: RootState) => state.clientLens.reviewerBackPeriod;
export const selectReviewerTimeWhen = createSelector(
  [selectReviewerTimeUnit, selectReviewerBackPeriod],
  (timeUnit, backPeriod) => {
    const when = backPeriod === 0 ? "this" : "last";
    return `${when}${timeUnit}`;
  }
);
export const selectReviewerStats = (state: RootState) => state.clientLens.reviewerStats;
export const selectReviewerCaseStats = (state: RootState) => state.clientLens.reviewerCaseStats;
export const selectTags = (state: RootState) => state.clientLens.tags;
export const selectFieldTagConfigs = (state: RootState) => state.clientLens.fieldTagConfigs;
export const selectSBRReviewerCaseStats = (state: RootState) => state.clientLens.sbrReviewerStats;
